import React, { useEffect, useState } from 'react';
import GenieCustomSectionMemoryConfig from './GenieCustomSectionMemoryConfig';
import {
    DEFAULT_GENIE_MEMORY_CONFIG,
    DEFAULT_GENIE_SETTINGS,
    DEFAULT_INJECTIONS_MEMORY_CONFIG,
    DEFAULT_LAB_TESTS_MEMORY_CONFIG,
    DEFAULT_MEDICATIONS_MEMORY_CONFIG,
    GenieTableType,
    GenieVersion,
} from '../../constants';
import pushGenie, { GenieMemoryConfig, GenieSettings } from '../../api/pushGenie';
import { getItemFromMisc, getMySingleDoctorId, MISC_KEYS } from '../../store/idbHandler';
import { useDispatch } from 'react-redux';
import { fetchMyGenie } from '../../store/actions/login';
import { notification, Tooltip } from 'antd';
import { captureError } from '../captureError';
import { track } from '../../tracking';
import { infoIconNew } from '../../components/common/Icons';

function GenieCustomSection() {
    const [initLoading, setInitLoading] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const dispatch = useDispatch();

    const [settings, setSettings] = useState<GenieSettings>(DEFAULT_GENIE_SETTINGS);
    const [genieMemoryConfig, setMemoryConfig] = useState<GenieMemoryConfig | undefined>(
        DEFAULT_GENIE_MEMORY_CONFIG,
    );
    const { customSections } = genieMemoryConfig || {};

    const { padElements } = settings || [];
    const docId = getMySingleDoctorId() || '';

    const getGenieSettings = async (shouldLoad: boolean = false) => {
        try {
            if (shouldLoad) setInitLoading(true);
            const settings = await getItemFromMisc<GenieSettings>(docId, MISC_KEYS.settings);
            const { genieMemoryConfig } = settings;
            const ALLOWED_MED_FIELDS = DEFAULT_MEDICATIONS_MEMORY_CONFIG.map((med) => med.id);
            const ALLOWED_INJ_FIELDS = DEFAULT_INJECTIONS_MEMORY_CONFIG.map((inj) => inj.id);
            const ALLOWED_LAB_TEST_FIELDS = DEFAULT_LAB_TESTS_MEMORY_CONFIG.map((lt) => lt.id);
            let clone;

            if (genieMemoryConfig) {
                clone = {
                    ...genieMemoryConfig,
                    medications: (genieMemoryConfig?.medications || []).filter((med) =>
                        ALLOWED_MED_FIELDS.includes(med.id),
                    ),
                    injections: (genieMemoryConfig?.injections || []).filter((inj) =>
                        ALLOWED_INJ_FIELDS.includes(inj.id),
                    ),
                    labTests: (genieMemoryConfig?.labTests || []).filter((lt) =>
                        ALLOWED_LAB_TEST_FIELDS.includes(lt.id),
                    ),
                };
            } else {
                clone = DEFAULT_GENIE_MEMORY_CONFIG;
            }

            setMemoryConfig(clone);
            setSettings(settings);
        } catch (err) {
            setMemoryConfig(undefined);
            setSettings(DEFAULT_GENIE_SETTINGS);
        } finally {
            if (shouldLoad) setInitLoading(false);
        }
    };
    const onSave = async () => {
        try {
            setLoading(true);
            const { version } = await getItemFromMisc<GenieVersion>(docId, MISC_KEYS.genieVersion);
            await pushGenie(docId, {
                id: docId,
                version,
                settings: {
                    ...settings,
                    genieMemoryConfig: genieMemoryConfig || {},
                },
            });
            dispatch(fetchMyGenie(undefined, undefined, getGenieSettings));
            notification.success({
                message: 'Successfully saved!',
                duration: 5,
            });
        } catch (e) {
            console.error('pushGenie => ', e);
            alert('Unable to save changes, please try again');
            captureError(e as Error);
            track('ERROR', { on: 'saveGenie', what: (e as Error).message });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getGenieSettings(true);
    }, []);

    return (
        <div style={{ width: '90%', margin: '0 auto', paddingBottom: 40 }}>
            <div className="flex flex-row items-center gap-8 p-16 pb-0">
                <p className="text-darwin-neutral-1000 heading-3 bold">Custom Sections</p>
                <Tooltip title="Turn off dropdown suggestions.">
                    {infoIconNew('fill-darwin-neutral_new-1000 w-14 h-14 cursor-pointer')}
                </Tooltip>
            </div>
            <GenieCustomSectionMemoryConfig
                config={customSections || []}
                type={GenieTableType.CUS}
                updateConfig={setMemoryConfig}
                padElements={padElements}
            />
            <div className="absolute bottom-12 right-16 left-16 bg-white z-20">
                <div className="flex items-center justify-end rounded-8 bg-actionPrimary bottom-24 inset-x-10 px-16 py-8 cta-row">
                    <button
                        onClick={onSave}
                        className="px-16 py-8 btn--small primary-btn-v2 tooltip-wrapper"
                        disabled={initLoading || loading}
                    >
                        {loading && (
                            <div className="w-20 h-20">
                                <svg className="circular-loader" viewBox="25 25 50 50">
                                    <circle
                                        className="loader-path"
                                        cx="50"
                                        cy="50"
                                        r="20"
                                        fill="none"
                                        stroke="#000000"
                                        stroke-width="3"
                                    ></circle>
                                </svg>
                            </div>
                        )}
                        <p>Save Changes</p>
                    </button>
                </div>
            </div>
        </div>
    );
}

export default GenieCustomSection;
